import { DrawingModel, type Handle } from "../DrawingModel";
import type { Chart } from "../../chart/Chart";
import { pointInRect, withAlpha } from "../geometry";

const RR = 3; // fixed 1:3 risk-to-reward
const round2 = (v: number) => Math.round(v * 4) / 4; // snap to MNQ 0.25 tick

// Single position tool with fixed 1:3 R/R.
// Two clicks: entry, then stop. Direction is auto-detected:
//   stop below entry  -> Long  (green)
//   stop above entry  -> Short (red)
// Take-profit is derived at 3R. anchors[0]=entry, [1]=stop, [2]=right-edge width.
export class Position extends DrawingModel {
  type = "position";

  private geom(chart: Chart) {
    const [entry, stop] = this.anchors;
    const e = entry.price;
    const s = stop.price;
    const long = s < e; // stop below entry => long
    const risk = Math.abs(e - s);
    const reward = risk * RR;
    const tp = long ? e + reward : e - reward;
    return { e, s, tp, long, risk, reward, color: long ? "#26a69a" : "#ef5350" };
  }

  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [entry, stop, width] = this.anchors;
    const { e, s, tp, long, risk, reward, color } = this.geom(chart);
    const x0 = this.X(entry, chart);
    const x1 = width ? this.X(width, chart) : x0 + 160;
    const lx = Math.min(x0, x1),
      rx = Math.max(x0, x1),
      w = rx - lx;
    const vp = chart.viewport;
    const yE = vp.yOfPrice(e);
    const ySL = vp.yOfPrice(s);
    const yTP = vp.yOfPrice(tp);

    // reward zone (entry -> tp) and risk zone (entry -> stop)
    ctx.fillStyle = withAlpha(color, 0.16);
    ctx.fillRect(lx, Math.min(yE, yTP), w, Math.abs(yTP - yE));
    ctx.fillStyle = withAlpha(color, 0.28);
    ctx.fillRect(lx, Math.min(yE, ySL), w, Math.abs(ySL - yE));

    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.strokeRect(lx, Math.min(yE, yTP), w, Math.abs(yTP - yE));
    ctx.strokeRect(lx, Math.min(yE, ySL), w, Math.abs(ySL - yE));

    // entry line
    ctx.strokeStyle = "#e6e9ef";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(lx, yE);
    ctx.lineTo(rx, yE);
    ctx.stroke();

    // readouts at the RIGHT edge of the box, one per line (entry / tp / sl), all white
    const dec = chart.priceDecimals;
    ctx.font = "10px -apple-system, system-ui, sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    const tx = rx + 6;
    ctx.fillStyle = "#ffffff";
    ctx.fillText(`TP ${tp.toFixed(dec)}  +${reward.toFixed(2)}`, tx, yTP);
    ctx.fillText(`Entry ${e.toFixed(dec)}`, tx, yE);
    ctx.fillText(`SL ${s.toFixed(dec)}  −${risk.toFixed(2)}`, tx, ySL);
  }

  // Order parameters derived from the box, for the "Fill order form" action.
  toOrder(): { side: "long" | "short"; entry: number; stopLoss: number; takeProfit: number } {
    const [entry, stop] = this.anchors;
    const e = entry.price;
    const s = stop.price;
    const long = s < e;
    const tp = long ? e + Math.abs(e - s) * RR : e - Math.abs(e - s) * RR;
    return { side: long ? "long" : "short", entry: round2(e), stopLoss: round2(s), takeProfit: round2(tp) };
  }

  handles(chart: Chart): Handle[] {
    const [entry, stop, width] = this.anchors;
    const x0 = this.X(entry, chart);
    const x1 = width ? this.X(width, chart) : x0 + 160;
    return [
      { x: x0, y: this.Y(entry, chart), kind: 0 }, // entry
      { x: x0, y: this.Y(stop, chart), kind: 1 }, // stop
      { x: x1, y: this.Y(entry, chart), kind: 2 }, // width
    ];
  }
  moveHandle(kind: number, px: number, py: number, chart: Chart) {
    const [entry, stop, width] = this.anchors;
    const time = chart.timeOfX(px);
    const price = chart.viewport.priceOfY(py);
    if (kind === 0) {
      entry.time = time;
      entry.price = price;
      stop.time = time;
    } else if (kind === 1) {
      stop.price = price;
    } else if (kind === 2 && width) {
      width.time = time;
    }
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [entry, stop, width] = this.anchors;
    const { tp } = this.geom(chart);
    const x0 = this.X(entry, chart);
    const x1 = width ? this.X(width, chart) : x0 + 160;
    const vp = chart.viewport;
    return pointInRect(px, py, x0, vp.yOfPrice(tp), x1, vp.yOfPrice(stop.price));
  }
}
